import { engineComponent } from "neo-common";
import { BaseElement } from "../Graphics/BaseElement";
import { ComponentTypes } from "../../ComponentTypes";

@engineComponent(ComponentTypes.DisplayObject)
export class ImageTextElement extends BaseElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public textAlign: string;

    public textBaseline: string;

    public text: string;

    public font: string;

    public bitmapText: PIXI.extras.BitmapText;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.textAlign = ImageTextElementAttributes.TEXT_ALIGN_VALUE.LEFT;
        this.textBaseline = ImageTextElementAttributes.TEXT_BASELINE_VALUE.TOP;
        this.text = "";
    }

    //******************************************************************************************************************
    //  CREATE, UPDATE
    //******************************************************************************************************************

    public createByNode(node: Element) {
        super.createByNode(node);

        if (this.bitmapText == null) {
            if (node.hasAttribute(ImageTextElementAttributes.FONT)) {
                if (node.hasAttribute(ImageTextElementAttributes.TEXT)) {
                    this.text = node.getAttribute(ImageTextElementAttributes.TEXT);
                }

                this.font = node.getAttribute(ImageTextElementAttributes.FONT);

                this.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });

                this.addChild(this.bitmapText);
            }
        }
    }

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(ImageTextElementAttributes.TEXT_ALIGN)) {
            this.textAlign = node.getAttribute(ImageTextElementAttributes.TEXT_ALIGN);

            this.realign();
        }

        if (node.hasAttribute(ImageTextElementAttributes.TEXT_ALIGN)) {
            this.textBaseline = node.getAttribute(ImageTextElementAttributes.TEXT_BASELINE);

            this.realign();
        }

        if (node.hasAttribute(ImageTextElementAttributes.TINT)) {
            let val = parseInt(node.getAttribute(ImageTextElementAttributes.TINT), 16);
            if (!isNaN(val)) {
                this.bitmapText.tint = val;
            }
        }
    }

    //******************************************************************************************************************
    //  REALIGN
    //******************************************************************************************************************

    public realign(): void {
        switch (this.textAlign) {
            case ImageTextElementAttributes.TEXT_ALIGN_VALUE.RIGHT:
                this.bitmapText.position.x = -this.bitmapText.width;
                break;
            case ImageTextElementAttributes.TEXT_ALIGN_VALUE.CENTER:
                this.bitmapText.position.x = -this.bitmapText.width * 0.5;
                break;
            default:
                this.bitmapText.position.x = 0;
                break;
        }

        switch (this.textBaseline) {
            case ImageTextElementAttributes.TEXT_BASELINE_VALUE.BOTTOM:
                this.bitmapText.position.y = -this.bitmapText.height;
                break;
            case ImageTextElementAttributes.TEXT_BASELINE_VALUE.MIDDLE:
                this.bitmapText.position.y = -this.bitmapText.height * 0.5;
                break;
            default:
                this.bitmapText.position.y = 0;
                break;
        }
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    public clone(): ImageTextElement {
        let element: ImageTextElement = new ImageTextElement();
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;

        element.font = this.font;
        element.text = this.text;
        element.textAlign = this.textAlign;
        element.textBaseline = this.textBaseline;

        element.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
        element.addChild(element.bitmapText);

        element.realign();
        element.markAsCloned();

        return element;
    }
}

export const ImageTextElementAttributes = {
    /**
     * The text to display.
     * @property Text
     * @type string
     * @default null
     */
    TEXT: "Text",
    /**
     * Bitmap font for text.
     * @property Font
     * @type string
     * @default null
     */
    FONT: "Font",
    /**
     * The horizontal text alignment. Any of "left", "right", and "center".
     * For detailed information view the whatwg spec. Default is "left".
     * @property TextAlign
     * @type string
     * @default left
     */
    TEXT_ALIGN: "TextAlign",
    TEXT_ALIGN_VALUE: {
        LEFT: "left",
        CENTER: "center",
        RIGHT: "right",
    },
    /**
     * The vertical alignment point on the text. Any of "top", "middle" or "bottom". Default is "top".
     * @property TextBaseline
     * @type string
     * @default top
     */
    TEXT_BASELINE: "TextBaseline",
    TEXT_BASELINE_VALUE: {
        TOP: "top",
        MIDDLE: "middle",
        BOTTOM: "bottom",
    },
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Texts/ImageTextElement.ts
 **/